/**
 * Cryptographically secure random utilities.
 * Replaces Math.random() to satisfy SonarQube security hotspot:
 * "Make sure that using this pseudorandom number generator is safe here."
 *
 * Uses the Web Crypto API (crypto.getRandomValues) which is available
 * in all modern browsers and Node.js >= 15.
 */

/**
 * Returns a cryptographically secure random float in [0, 1).
 * Drop-in replacement for Math.random().
 */
export function secureRandom(): number {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
}

/**
 * Returns a cryptographically secure random integer in [0, max).
 * Replacement for Math.floor(Math.random() * max).
 */
export function secureRandomInt(max: number): number {
  return Math.floor(secureRandom() * max);
}

/**
 * Generates a cryptographically secure unique ID string.
 * Replacement for: Date.now().toString() + Math.random().toString(36).substring(2, 9)
 * or: `chart-${Date.now()}-${Math.random()}`
 */
export function secureUniqueId(prefix = ""): string {
  const uuid = crypto.randomUUID(); // available in all modern browsers
  return prefix ? `${prefix}-${uuid}` : uuid;
}
